import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";
import { morph } from "@theme/morph";

/**
 * <subscription-selector>
 *
 * Theme-native vervanger voor de Firmhouse app-block. Rendert een toggle
 * (eenmalig vs. abonnement) en een frequentie-keuze op basis van de
 * selling plans van het product. De gekozen selling_plan wordt via het
 * `form`-attribuut aan het product-formulier gehangen, zodat de regel als
 * abonnement in de cart belandt.
 *
 * Koppelt in op het theme:
 * - prijs-DOM van het `_product-price` block (.price-container / .price__sale)
 * - de quantity-selector in `_buy-buttons`
 * - de add-to-cart knop (.add-to-cart-button)
 * - het `variant:update` / `variant:selected` event van de variant-picker
 */
class SubscriptionSelector extends Component {
  #section = null;
  #form = null;
  #variant = { id: "", price: 0, compare_at_price: 0 };
  #mainPriceModified = false;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest(".shopify-section");
    this.#variant.id = this.dataset.variantId || "";
    this.#variant.price = Number(this.dataset.variantPrice) || 0;
    this.#variant.compare_at_price = Number(this.dataset.variantCompare) || 0;

    this.#form = this.#productForm();

    // Safety net: vlak vóór elke add-to-cart submit garanderen dat er precies één
    // selling_plan-veld enabled is (en checked in abonnementsmodus). Document-capture
    // draait gegarandeerd vóór het bouwen van de FormData in product-form-component.
    document.addEventListener("submit", this.#onFormSubmitCapture, { capture: true });

    // Onthoud de keuze (bijv. vanaf een toggle elders) + de default-instelling.
    const stored = this.#storageKey ? localStorage.getItem(this.#storageKey) : null;
    const startSubscribed =
      stored === "true" || (stored === null && this.dataset.defaultSubscription === "true");

    this.#setMode(startSubscribed, { persist: false, silent: true });

    // Reageer op variantwissel binnen de sectie.
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    document.removeEventListener("submit", this.#onFormSubmitCapture, { capture: true });
  }

  /* ----------------------------------------------------------------- config */

  get #storageKey() {
    return this.dataset.storageKey || "";
  }
  get #syncMainPrice() {
    return this.dataset.syncMainPrice !== "false";
  }
  get #adjustQuantity() {
    return this.dataset.adjustQuantity !== "false";
  }
  get #changeButtonText() {
    return this.dataset.changeButtonText !== "false";
  }
  get #subscribeButtonLabel() {
    return this.dataset.buttonText || "Start abonnement";
  }
  get #savingsLabel() {
    return this.dataset.savingsLabel || "Bespaar";
  }

  /* ---------------------------------------------------------------- queries */

  get #frequencyRadios() {
    return Array.from(this.querySelectorAll(".subscription-selector__frequency-radio"));
  }
  get #onetimeInput() {
    return this.querySelector(".subscription-selector__onetime-input");
  }
  get #isSubscription() {
    return this.querySelector('input[name="purchase_option"][value="subscription"]')?.checked || false;
  }
  #selectedFrequency() {
    return this.#frequencyRadios.find((radio) => radio.checked) || null;
  }

  /* --------------------------------------------------- declaratieve handlers */

  /** @param {Event} event */
  handlePurchaseOptionChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    this.#setMode(target.value === "subscription", { persist: true });
  }

  handleFrequencyChange() {
    this.#syncFrequencyActiveStates();
    this.#syncCartInput();
    this.#applyQuantity();
    this.#applyMainPrice();
    this.#updateBenefits();
  }

  /** Toont de juiste benefit-lijst (1 vs 3 maanden) o.b.v. de gekozen frequentie. */
  #updateBenefits() {
    const benefits = this.refs.benefits;
    if (!benefits) return;
    const checked = this.#selectedFrequency();
    const target = checked && checked.dataset.quantity !== "1" ? "3" : "1";
    benefits.querySelectorAll("[data-benefits-plan]").forEach((list) => {
      list.hidden = list.getAttribute("data-benefits-plan") !== target;
    });
  }

  /* ------------------------------------------------------------------- modes */

  #setMode(isSub, { persist = true, silent = false } = {}) {
    const subRadio = this.querySelector('input[name="purchase_option"][value="subscription"]');
    const oneRadio = this.querySelector('input[name="purchase_option"][value="onetime"]');
    if (subRadio) subRadio.checked = isSub;
    if (oneRadio) oneRadio.checked = !isSub;

    this.querySelectorAll(".subscription-selector__option").forEach((option) => {
      const radio = option.querySelector('input[name="purchase_option"]');
      option.classList.toggle("subscription-selector__option--active", Boolean(radio?.checked));
    });

    this.refs.frequencies?.classList.toggle("subscription-selector__frequencies--hidden", !isSub);
    this.refs.benefits?.classList.toggle("subscription-selector__benefits--hidden", !isSub);

    this.#enforceSellingPlanState({ isSub });
    this.#syncFrequencyActiveStates();
    this.#syncCartInput();

    if (persist && this.#storageKey) localStorage.setItem(this.#storageKey, String(isSub));

    this.#applyQuantity();
    this.#updateButtonText(isSub);
    if (isSub) this.#applyMainPrice();
    else this.#restoreMainPrice();
    if (isSub) this.#updateBenefits();

    if (!silent) {
      document.dispatchEvent(
        new CustomEvent("subscription:mode-changed", { bubbles: true, detail: { enabled: isSub } })
      );
    }
  }

  #syncFrequencyActiveStates() {
    this.querySelectorAll(".subscription-selector__frequency").forEach((card) => {
      const radio = card.querySelector(".subscription-selector__frequency-radio");
      card.classList.toggle("subscription-selector__frequency--active", Boolean(radio?.checked));
    });
  }

  /**
   * Garandeert precies één selling_plan-veld in de FormData:
   * - abonnement: frequentie-radio's enabled, precies één checked, leeg veld disabled
   * - eenmalig:   frequentie-radio's disabled + unchecked, leeg veld enabled
   * Disabled velden gaan niet mee in FormData, dus dit borgt de juiste keuze.
   * @param {{ isSub?: boolean }} [opts]
   */
  #enforceSellingPlanState({ isSub = this.#isSubscription } = {}) {
    const radios = this.#frequencyRadios;
    if (isSub) {
      radios.forEach((radio) => (radio.disabled = false));
      if (!radios.some((radio) => radio.checked) && radios[0]) radios[0].checked = true;
      if (this.#onetimeInput) this.#onetimeInput.disabled = true;
    } else {
      radios.forEach((radio) => {
        radio.disabled = true;
        radio.checked = false;
      });
      if (this.#onetimeInput) this.#onetimeInput.disabled = false;
    }
  }

  /** @param {Event} event */
  #onFormSubmitCapture = (event) => {
    const target = event.target;
    if (
      target instanceof HTMLFormElement &&
      (target === this.#form || (this.dataset.formId && target.id === this.dataset.formId))
    ) {
      this.#enforceSellingPlanState();
      this.#syncCartInput(target);
    }
  };

  /* ---------------------------------------------------------------- quantity */

  #applyQuantity() {
    if (!this.#adjustQuantity) return;
    const isSub = this.#isSubscription;
    const radio = this.#selectedFrequency();
    const qty = isSub && radio ? Number(radio.dataset.quantity) || 1 : 1;
    this.#setFormQuantity(qty);
    // In abonnementsmodus bepaalt het plan de hoeveelheid: selector verbergen.
    this.#toggleQuantitySelector(!isSub);
  }

  #setFormQuantity(qty) {
    const form = this.#productForm();
    if (!form) return;
    const input = /** @type {HTMLInputElement|null} */ (
      form.querySelector('.quantity-input, input[name="quantity"]')
    );
    if (input) {
      input.value = String(qty);
      input.setAttribute("value", String(qty));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "quantity";
    hidden.value = String(qty);
    form.appendChild(hidden);
  }

  #toggleQuantitySelector(visible) {
    const selector = this.#section?.querySelector(
      ".product-form-buttons .quantity-selector, .product-form-buttons quantity-selector-component"
    );
    if (selector instanceof HTMLElement) selector.style.display = visible ? "" : "none";
  }

  /* ------------------------------------------------------------- button text */

  #updateButtonText(isSub) {
    if (!this.#changeButtonText) return;
    this.#section
      ?.querySelectorAll(".add-to-cart-button .add-to-cart-text__content")
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (!el.dataset.originalText) el.dataset.originalText = el.textContent?.trim() || "";
        el.textContent = isSub ? this.#subscribeButtonLabel : el.dataset.originalText;
      });
  }

  /* -------------------------------------------------------------- main price */

  #mainPriceContainer() {
    return this.#section?.querySelector(".product-price-block [ref=\"priceContainer\"]") || null;
  }

  #applyMainPrice() {
    if (!this.#syncMainPrice || !this.#isSubscription) return;
    const radio = this.#selectedFrequency();
    const container = this.#mainPriceContainer();
    if (!radio || !container) return;

    // Hoofdprijs toont de prijs per potje (matcht de frequentie-kaart), niet het
    // totaal. data-display-total/compare-total zijn het totaal; deel door het aantal.
    const qty = Number(radio.dataset.quantity) || 1;
    const display = Math.round((Number(radio.dataset.displayTotal) || 0) / qty);
    const compare = Math.round((Number(radio.dataset.compareTotal) || 0) / qty);
    const savings = Math.max(0, compare - display);

    container.classList.add("price--on-sale");

    const saleWrap = container.querySelector(".price__sale");
    const saleEl = container.querySelector(".price__sale .price--sale") || container.querySelector(".price--sale");
    const compareEl = container.querySelector(".price__sale .compare-at-price");
    if (saleEl) saleEl.textContent = this.#money(display);
    if (compareEl) {
      compareEl.textContent = compare > display ? this.#money(compare) : "";
    }
    const regularEl = container.querySelector(".price__regular .price");
    if (regularEl && !regularEl.textContent?.includes("–")) regularEl.textContent = this.#money(display);

    if (saleWrap) {
      let badge = saleWrap.querySelector(".subscription-selector__savings");
      if (savings > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "subscription-selector__savings";
          saleWrap.appendChild(badge);
        }
        badge.textContent = `${this.#savingsLabel} ${this.#money(savings)}`;
      } else if (badge) {
        badge.remove();
      }
    }
    this.#mainPriceModified = true;
  }

  #restoreMainPrice() {
    if (!this.#syncMainPrice || !this.#mainPriceModified) return;
    const container = this.#mainPriceContainer();
    container?.querySelector(".subscription-selector__savings")?.remove();

    // Laat het theme zelf de echte (eenmalige) prijs terugzetten.
    const productPrice = this.#section?.querySelector(".product-price-block");
    if (productPrice && this.#variant.price) {
      productPrice.dispatchEvent(
        new CustomEvent("price:update-optimistic", {
          detail: {
            variant: {
              id: this.#variant.id,
              price: this.#variant.price,
              compare_at_price: this.#variant.compare_at_price,
            },
          },
        })
      );
    }
    this.#mainPriceModified = false;
  }

  /* ---------------------------------------------------------- variant events */

  #onVariantSelected = (event) => {
    const variant = event.detail?.resource;
    if (variant && typeof variant.price === "number") {
      this.#variant.id = String(variant.id ?? this.#variant.id);
      this.#variant.price = variant.price;
      this.#variant.compare_at_price = variant.compare_at_price || 0;
    }
  };

  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (variant) {
      this.#variant.id = String(variant.id ?? this.#variant.id);
      this.#variant.price = variant.price ?? this.#variant.price;
      this.#variant.compare_at_price = variant.compare_at_price ?? 0;
      this.dataset.variantPrice = String(this.#variant.price);
    }

    const html = event.detail?.data?.html;
    if (html) this.#morphFromHtml(html);
    else this.#recomputeFromVariant();

    // Het theme herzet eerst de eenmalige prijs; pas daarna onze prijs weer toe.
    requestAnimationFrame(() => {
      if (this.#isSubscription) {
        this.#mainPriceModified = false;
        this.#applyMainPrice();
      }
    });
  };

  #morphFromHtml(html) {
    const incoming = html.querySelector("subscription-selector .subscription-selector__frequencies");
    const current = this.refs.frequencies;
    if (!incoming || !current) return;
    // Bewaar de gekozen frequentie; verse server-HTML komt binnen met de
    // standaard-state (disabled + niets checked).
    const prevValue = this.#selectedFrequency()?.value || null;
    morph(current, incoming, { childrenOnly: true });
    if (prevValue) {
      const match = this.#frequencyRadios.find((radio) => radio.value === prevValue);
      if (match) match.checked = true;
    }
    // Herstel enabled/checked zodat de selling_plan ook na een variantwissel meegaat.
    this.#enforceSellingPlanState();
    this.#syncFrequencyActiveStates();
  }

  #recomputeFromVariant() {
    const unit = this.#variant.price;
    if (!unit) return;
    this.#frequencyRadios.forEach((radio) => {
      const pct = Number(radio.dataset.discountPct) || 0;
      const qty = Number(radio.dataset.quantity) || 1;
      const perDelivery = Math.round(unit * (1 - pct / 100));
      const display = perDelivery * qty;
      const compare = unit * qty;
      radio.dataset.displayTotal = String(display);
      radio.dataset.compareTotal = String(compare);

      const card = radio.closest(".subscription-selector__frequency");
      const priceEl = card?.querySelector(".subscription-selector__frequency-price");
      const compareEl = card?.querySelector(".subscription-selector__frequency-compare-price");
      if (priceEl) priceEl.textContent = this.#money(perDelivery);
      if (compareEl) compareEl.textContent = unit > perDelivery ? this.#money(unit) : "";
    });
  }

  /* ----------------------------------------------------------------- helpers */

  #productForm() {
    // Voorkeur: het form-id uit Liquid (BuyButtons-ProductForm-<section.id>).
    const id = this.dataset.formId;
    if (id) {
      const byId = document.getElementById(id);
      if (byId) return byId;
    }
    // Robuuste fallback: specifiek het koop-formulier in deze sectie. NIET het
    // eerste 'add-to-cart-form' — een quiz-block heeft er ook een.
    return (
      this.#section?.querySelector('form[id^="BuyButtons-ProductForm-"]') ||
      this.#section?.querySelector("product-form-component form") ||
      this.#section?.querySelector('form[data-type="add-to-cart-form"]') ||
      null
    );
  }

  /**
   * Schrijft de keuze als één verborgen veld DIRECT in het koopformulier, zodat
   * het altijd in new FormData(form) zit — onafhankelijk van form-attribuut-
   * koppeling. De externe radio's zijn puur UI (niet form-gekoppeld).
   * @param {HTMLFormElement|null} [form]
   */
  #syncCartInput(form) {
    form = form || this.#form || this.#productForm();
    if (!form) return;
    let input = form.querySelector('input[name="selling_plan"][data-ss-cart]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "selling_plan";
      input.setAttribute("data-ss-cart", "");
      form.appendChild(input);
    }
    const radio = this.#selectedFrequency();
    input.value = this.#isSubscription && radio ? radio.value : "";
  }

  #money(cents) {
    return formatCurrency(cents, window.DiekTheme?.moneyFormat || "€{{amount}}");
  }
}

if (!customElements.get("subscription-selector")) {
  customElements.define("subscription-selector", SubscriptionSelector);
}
